import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Input, Spinner } from 'reactstrap';
import axios from 'axios';

/**
 * TutoredModeAIChat - Prep AI chat for tutored mode questions
 * Sends messages to backend which proxies to Gemini AI
 * Restricts AI responses to only answer questions related to the current question
 * Persists chat history per question when testId and questionId are provided
 */
const TutoredModeAIChat = ({
  questionText,
  explanationText,
  answers,
  correctAnswerId,
  userAnswerId,
  passageText,
  testId,
  questionId,
  variant = 'standard' // 'standard' or 'pmr'
}) => {
  const activeSpecialty = useSelector(state => state.specialty?.activeSpecialty || 'pmr');
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const canPersist = Boolean(testId && questionId);

  // Fetch chat history when testId/questionId change
  useEffect(() => {
    if (!canPersist) return;

    let cancelled = false;
    setMessages([]);
    setIsLoadingHistory(true);

    axios.post('/prepai/fetchHistory', {
      testId,
      questionId,
      specialty: activeSpecialty,
    })
      .then(res => {
        if (!cancelled && res.data.messages?.length > 0) {
          setMessages(res.data.messages);
          setIsExpanded(true);
        }
      })
      .catch(err => {
        console.error('Failed to fetch chat history:', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingHistory(false);
      });

    return () => { cancelled = true; };
  }, [testId, questionId, activeSpecialty, canPersist]);

  // Fire-and-forget save to backend
  const saveHistory = useCallback((msgs) => {
    if (!canPersist) return;
    axios.post('/prepai/saveHistory', {
      testId,
      questionId,
      messages: msgs,
      specialty: activeSpecialty,
    }).catch(err => {
      console.error('Failed to save chat history:', err);
    });
  }, [testId, questionId, activeSpecialty, canPersist]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const thinkingMessages = [
    'Thinking...',
    'Analyzing the question...',
    'Reviewing the concepts...',
    'Preparing a response...',
    'Looking into this...',
    'Processing your question...',
    'Gathering my thoughts...',
    'Considering the details...',
  ];
  const [thinkingText, setThinkingText] = useState('Thinking...');

  // Call backend Prep AI endpoint
  const callPrepAI = async (userMessage, conversationHistory) => {
    const res = await axios.post('/prepai/chat', {
      message: userMessage,
      conversationHistory,
      specialty: activeSpecialty,
      questionContext: {
        questionText,
        explanationText,
        answers,
        correctAnswerId,
        userAnswerId,
        passageText,
      },
    });
    return res.data.response;
  };

  // Handle sending a message
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    // First message: no delay. Subsequent messages: 5-8 second random delay.
    const isFirstMessage = messages.length === 0;
    const picked = new Set();
    const pickThinking = () => {
      const available = thinkingMessages.filter((_, i) => !picked.has(i));
      if (available.length === 0) picked.clear();
      const pool = available.length > 0 ? available : thinkingMessages;
      const idx = Math.floor(Math.random() * pool.length);
      picked.add(thinkingMessages.indexOf(pool[idx]));
      return pool[idx];
    };
    setThinkingText(pickThinking());
    const thinkingInterval = setInterval(() => setThinkingText(pickThinking()), 3000);

    if (!isFirstMessage) {
      const delay = Math.floor(Math.random() * 4 + 5) * 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    try {
      const aiResponse = await callPrepAI(userMessage, messages);
      const updatedMessages = [...newMessages, { role: 'assistant', content: aiResponse }];
      setMessages(updatedMessages);
      // Save after successful AI response (fire-and-forget)
      saveHistory(updatedMessages);
    } catch (error) {
      console.error('AI Chat Error:', error);
      const status = error.response?.status;
      const serverMessage = error.response?.data?.error?.message;
      // Error messages are not persisted
      setMessages([...newMessages, {
        role: 'assistant',
        content: (status === 429 || status === 503)
          ? (serverMessage || 'Prep AI is experiencing high demand right now. Please try again in a moment.')
          : (serverMessage || 'Something went wrong with Prep AI. Please try again.')
      }]);
    } finally {
      clearInterval(thinkingInterval);
      setIsLoading(false);
    }
  };

  // Handle key press (Enter to send)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Style configurations
  const isPmr = variant === 'pmr';
  const headerBg = isPmr ? '#1e6091' : '#5e72e4';
  const messageBubbleBg = isPmr ? '#e8f4f8' : '#f0f4ff';

  if (!isExpanded) {
    return (
      <div
        className={`mt-3 ${isPmr ? 'p-2' : 'p-3 border rounded'}`}
        style={isPmr ? {
          backgroundColor: '#f8f9fa',
          borderLeft: '3px solid #1e6091',
          fontSize: '0.85rem'
        } : {
          backgroundColor: '#f8f9fa'
        }}
      >
        <Button
          color={isPmr ? 'primary' : 'info'}
          size="sm"
          onClick={() => setIsExpanded(true)}
          style={isPmr ? {
            backgroundColor: '#1e6091',
            borderColor: '#1e6091',
            fontSize: '0.8rem'
          } : {}}
        >
          <i className="fas fa-robot mr-2"></i>
          Ask Prep AI About This Question
        </Button>
        <small className="text-muted d-block mt-1">
          Get help understanding the concepts in this question
        </small>
      </div>
    );
  }

  return (
    <div
      className={`mt-3 ${isPmr ? '' : 'border rounded'}`}
      style={{
        backgroundColor: '#ffffff',
        overflow: 'hidden',
        ...(isPmr ? { border: '1px solid #1e6091' } : {})
      }}
    >
      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ backgroundColor: headerBg }}
      >
        <span className="text-white font-weight-bold" style={{ fontSize: '0.85rem' }}>
          <i className="fas fa-robot mr-2"></i>
          Prep AI
        </span>
        <Button
          close
          className="text-white"
          onClick={() => setIsExpanded(false)}
          style={{ opacity: 1 }}
        />
      </div>

      {/* Messages Area */}
      <div
        className="p-3"
        style={{
          maxHeight: '300px',
          overflowY: 'auto',
          minHeight: '150px',
          backgroundColor: '#fafafa'
        }}
      >
        {isLoadingHistory ? (
          <div className="text-center text-muted py-4">
            <Spinner size="sm" className="mr-2" />
            Loading conversation...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-comments fa-2x mb-2" style={{ opacity: 0.5 }}></i>
            <p className="mb-0" style={{ fontSize: '0.85rem' }}>
              Ask any question about the concepts covered here.
            </p>
            <small>I can help explain the explanation, discuss related topics, or clarify confusing parts.</small>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div
              key={idx}
              className={`mb-2 d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div
                className="px-3 py-2 rounded"
                style={{
                  maxWidth: '85%',
                  backgroundColor: msg.role === 'user' ? headerBg : messageBubbleBg,
                  color: msg.role === 'user' ? '#ffffff' : '#333333',
                  fontSize: '0.85rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {msg.content}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="d-flex justify-content-start mb-2">
            <div
              className="px-3 py-2 rounded"
              style={{
                backgroundColor: messageBubbleBg,
                fontSize: '0.85rem'
              }}
            >
              <Spinner size="sm" className="mr-2" />
              {thinkingText}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-top p-2 d-flex align-items-center" style={{ backgroundColor: '#f5f5f5' }}>
        <Input
          type="text"
          placeholder="Ask about this question..."
          maxLength={1000}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || isLoadingHistory}
          innerRef={inputRef}
          style={{ fontSize: '0.85rem' }}
          className="mr-2"
        />
        <Button
          color={isPmr ? 'primary' : 'info'}
          size="sm"
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading || isLoadingHistory}
          style={isPmr ? {
            backgroundColor: '#1e6091',
            borderColor: '#1e6091'
          } : {}}
        >
          <i className="fas fa-paper-plane"></i>
        </Button>
      </div>

      {/* Disclaimer */}
      <div className="px-3 py-1 border-top" style={{ backgroundColor: '#f9f9f9' }}>
        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
          <i className="fas fa-info-circle mr-1"></i>
          AI responses are for learning purposes only. Always verify with authoritative sources.
        </small>
      </div>
    </div>
  );
};

export default TutoredModeAIChat;
